import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable'
import { FileTreePanel } from './FileTreePanel'
import { CodeViewer } from './CodeViewer'
import { Code2, AlertCircle, Loader2 } from 'lucide-react'
import type { Project } from '@/types/conversations'

interface CodeTabProps {
  project: Project
  onInsertPrompt?: (prompt: string) => void
}

export function CodeTab({ project, onInsertPrompt }: CodeTabProps) {
  const { t } = useTranslation('conversations')
  const [selectedPath, setSelectedPath] = useState<string | null>(null)

  // Check if instance is running
  const isInstanceReady = project.runtime_state === 'running'
  const isInstanceStopped = project.runtime_state === 'stopped'
  const isInstanceStarting = project.runtime_state === 'starting'

  // Instance not ready - show appropriate message
  if (!isInstanceReady) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-card">
        {isInstanceStopped ? (
          <>
            <div className="h-16 w-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-4">
              <Code2 className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <h3 className="font-semibold text-lg text-foreground mb-2">
              {t('code.instanceStopped.title')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {t('code.instanceStopped.description')}
            </p>
          </>
        ) : isInstanceStarting ? (
          <>
            <div className="h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
              <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
            <h3 className="font-semibold text-lg text-foreground mb-2">
              {t('code.instanceStarting.title')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {t('code.instanceStarting.description')}
            </p>
          </>
        ) : (
          <>
            <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center mb-4">
              <AlertCircle className="h-8 w-8 text-gray-600 dark:text-gray-400" />
            </div>
            <h3 className="font-semibold text-lg text-foreground mb-2">
              {t('code.instanceNotReady.title')}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {t('code.instanceNotReady.description')}
            </p>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="relative h-full">
      <div className="absolute inset-0">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* File Tree Panel */}
          <ResizablePanel defaultSize={25} minSize={15} maxSize={40}>
            <FileTreePanel
              projectId={project.id}
              selectedPath={selectedPath}
              onFileSelect={setSelectedPath}
              onInsertPrompt={onInsertPrompt}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Code Viewer Panel */}
          <ResizablePanel defaultSize={75} minSize={50}>
            <div className="h-full">
              <CodeViewer
                projectId={project.id}
                filePath={selectedPath}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  )
}
